//  ----------      START   IMPORT  BLOCK       ----------
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
//  Widgets
import { Form, Button, Card, Alert } from "react-bootstrap";
//  Context
import { useLanguage } from "../../API/Context/LanguageContext";
import { useAuthFlow } from "../../API/Context/SessionContext";
//  Config
import { FRONTEND_ROUTES } from "../../ConfigFiles/Routes/frontend_routes";
import { VocabAuth as vocab } from "../../ConfigFiles/multilingual/VocabAuth";
import "./LoginWidget.css";
//  ----------      END   IMPORT  BLOCK       ----------

// Derive key type directly from the imported object alias
type VocabAuthKey = keyof typeof vocab;

interface LoginWidgetProps {
  targetUsername?: string | undefined;
  targetTab?: string;
}

const LoginWidget: React.FC<LoginWidgetProps> = ({
  targetUsername,
  targetTab = "privacy",
}) => {
  const { lan } = useLanguage();
  const { handleLoginFlow } = useAuthFlow();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [userType, setUserType] = useState(false); // false = Regular (Patient), true = Professional
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getVocab = (key: VocabAuthKey, defaultText = key) => {
    return vocab[key]?.[lan] ?? defaultText;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const success = await handleLoginFlow(
        formData.username,
        formData.password,
        userType
      );

      if (success) {
        let destination = FRONTEND_ROUTES.dashboard;
        if (targetUsername) {
          destination += `?tab=${encodeURIComponent(targetTab)}&add_user=${encodeURIComponent(targetUsername)}`;
        }
        navigate(destination);
      } else {
        setError(getVocab("login_failed"));
      }
    } catch (err) {
      console.error("Error during login (component handler):", err);
      setError(getVocab("login_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="shadow-lg border-0 p-3 p-md-4 w-100">
      {error && <Alert variant="danger">{error}</Alert>}

      {/* --- ROLE TOGGLE --- */}
      <div
        className="login-toggle-container mb-4"
        onClick={() => setUserType(!userType)}
      >
        <div className={`toggle-slider ${userType ? "is-professional" : ""}`} />

        <div className={`toggle-icon ${!userType ? "active" : ""}`}>
          <span className="toggle-label">{getVocab("login_patient_label")}</span>
        </div>

        <div className={`toggle-icon ${userType ? "active" : ""}`}>
          <span className="toggle-label">{getVocab("login_professional_label")}</span>
        </div>
      </div>

      {/* --- FORM --- */}
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>{getVocab("username")}</Form.Label>
          <Form.Control
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>{getVocab("password")}</Form.Label>
          <Form.Control
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Button
          type="submit"
          variant="primary"
          className="w-100 mb-3"
          disabled={isLoading}
        >
          {isLoading ? "Iniciando..." : getVocab("login")}
        </Button>
      </Form>

      <div className="text-center">
        <span>{getVocab("no_account")} </span>
        <Button
          variant="link"
          className="p-0"
          onClick={() => navigate(FRONTEND_ROUTES.register)}
        >
          {getVocab("register")}
        </Button>
      </div>
    </Card>
  );
};

export default LoginWidget;